import { Box, FormHelperText, IconButton, InputAdornment, SxProps, TextField } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useEffect, useState } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const INVALID_DATE = 'Invalid date';

type InternalDatePickerProps = {
    min: string;
    max: string;
    dateValue: string;
    onDateChange: (date: string) => void;
    sx?: SxProps;
    variant?: 'standard' | 'outlined';
    disabled?: boolean;
    intervalError?: boolean;
};

const AuditDatePicker = ({
    dateValue,
    min,
    max,
    intervalError,
    disabled,
    onDateChange,
    sx,
    variant
}: InternalDatePickerProps) => {
    const [open, setOpen] = useState(false);
    const [error, setError] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const parseDateString = (dateString: string): dayjs.Dayjs => {
        return dayjs(dateString);
    };

    useEffect(() => {
        setError(
            dateValue.toString().toLowerCase() === INVALID_DATE.toLowerCase() ||
                isNaN(Date.parse(dateValue)) ||
                new Date(dateValue).getFullYear() < 1900
        );
    }, [dateValue]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'block' }}>
                <DatePicker
                    disabled={disabled}
                    open={open}
                    onOpen={handleOpen}
                    onClose={handleClose}
                    onChange={(date) => {
                        if (date === null) {
                            setError(true);
                            return;
                        }

                        if (setError) setError(false);

                        onDateChange(date.format('YYYY-MM-DD'));
                    }}
                    value={parseDateString(dateValue)}
                    minDate={min ? parseDateString(min) : undefined}
                    maxDate={max ? parseDateString(max) : undefined}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="start">
                                <IconButton edge="end" onClick={handleOpen}>
                                    <EventIcon color="secondary" />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    renderInput={(props) => (
                        <TextField
                            required
                            margin="none"
                            helperText={
                                error && !intervalError ? 'Please select a valid date.' : ''
                            }
                            variant={variant ? variant : 'outlined'}
                            InputLabelProps={{
                                style: { fontSize: '0.875rem', fontWeight: 'bold' }
                            }}
                            fullWidth
                            sx={
                                sx
                                    ? sx
                                    : {
                                          '& .MuiInputBase-root': {
                                              height: '2.5rem',
                                              textAlign: 'center'
                                          }
                                      }
                            }
                            {...props}
                        />
                    )}
                />

                {intervalError ? (
                    <FormHelperText error>Please select a valid interval.</FormHelperText>
                ) : null}
            </Box>
        </LocalizationProvider>
    );
};

export default AuditDatePicker;
